/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CoreApiService } from '../core-api-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { deleteRouterById } from '../fn/routers/delete-router-by-id';
import { DeleteRouterById$Params } from '../fn/routers/delete-router-by-id';
import { deleteVpnConnection } from '../fn/routers/delete-vpn-connection';
import { DeleteVpnConnection$Params } from '../fn/routers/delete-vpn-connection';
import { getAllRouters } from '../fn/routers/get-all-routers';
import { GetAllRouters$Params } from '../fn/routers/get-all-routers';
import { getMasterPasswordById } from '../fn/routers/get-master-password-by-id';
import { GetMasterPasswordById$Params } from '../fn/routers/get-master-password-by-id';
import { getRouterById } from '../fn/routers/get-router-by-id';
import { GetRouterById$Params } from '../fn/routers/get-router-by-id';
import { PageDto } from '../models/page-dto';
import { postActivation } from '../fn/routers/post-activation';
import { PostActivation$Params } from '../fn/routers/post-activation';
import { postCertificateRenewal } from '../fn/routers/post-certificate-renewal';
import { PostCertificateRenewal$Params } from '../fn/routers/post-certificate-renewal';
import { postFirmwareActivation } from '../fn/routers/post-firmware-activation';
import { PostFirmwareActivation$Params } from '../fn/routers/post-firmware-activation';
import { postFirmwareForceUpdate } from '../fn/routers/post-firmware-force-update';
import { PostFirmwareForceUpdate$Params } from '../fn/routers/post-firmware-force-update';
import { postFirmwareUpdate } from '../fn/routers/post-firmware-update';
import { PostFirmwareUpdate$Params } from '../fn/routers/post-firmware-update';
import { postNewList } from '../fn/routers/post-new-list';
import { PostNewList$Params } from '../fn/routers/post-new-list';
import { postNewRouter } from '../fn/routers/post-new-router';
import { PostNewRouter$Params } from '../fn/routers/post-new-router';
import { postVpnConnection } from '../fn/routers/post-vpn-connection';
import { PostVpnConnection$Params } from '../fn/routers/post-vpn-connection';
import { RouterCreationResponse } from '../models/router-creation-response';
import { RouterDto } from '../models/router-dto';
import { routersIdVpnConnectionUsernameDelete } from '../fn/routers/routers-id-vpn-connection-username-delete';
import { RoutersIdVpnConnectionUsernameDelete$Params } from '../fn/routers/routers-id-vpn-connection-username-delete';
import { unlinkRouter } from '../fn/routers/unlink-router';
import { UnlinkRouter$Params } from '../fn/routers/unlink-router';
import { updateRouter } from '../fn/routers/update-router';
import { UpdateRouter$Params } from '../fn/routers/update-router';

@Injectable({ providedIn: 'root' })
export class RoutersAPI extends CoreApiService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `postFirmwareActivation()` */
  static readonly PostFirmwareActivationPath = '/firmware/update/{routerId}/allow-activation';

  /**
   * Allow the router to activate the previously downloaded firmware.
   *
   * The router will start with the firmware activation after the next status communication
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postFirmwareActivation()` instead.
   *
   * This method doesn't expect any request body.
   */
  postFirmwareActivation$Response(params: PostFirmwareActivation$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return postFirmwareActivation(this.http, this.rootUrl, params, context);
  }

  /**
   * Allow the router to activate the previously downloaded firmware.
   *
   * The router will start with the firmware activation after the next status communication
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `postFirmwareActivation$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postFirmwareActivation(params: PostFirmwareActivation$Params, context?: HttpContext): Observable<void> {
    return this.postFirmwareActivation$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `postFirmwareUpdate()` */
  static readonly PostFirmwareUpdatePath = '/firmware/update/{routerId}/allow-download';

  /**
   * Allow the router to download the newest firmware.
   *
   * The router will begin to download the newest firmware on the next status communication
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postFirmwareUpdate()` instead.
   *
   * This method doesn't expect any request body.
   */
  postFirmwareUpdate$Response(params: PostFirmwareUpdate$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return postFirmwareUpdate(this.http, this.rootUrl, params, context);
  }

  /**
   * Allow the router to download the newest firmware.
   *
   * The router will begin to download the newest firmware on the next status communication
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `postFirmwareUpdate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postFirmwareUpdate(params: PostFirmwareUpdate$Params, context?: HttpContext): Observable<void> {
    return this.postFirmwareUpdate$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `postFirmwareForceUpdate()` */
  static readonly PostFirmwareForceUpdatePath = '/firmware/update/{routerId}/force-update/{firmwareId}';

  /**
   * Forces the router to update to a defined firmware.
   *
   * The router will start with the firmware update process after the next status communication. The user does not need to download or activate the firmware afterwards.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postFirmwareForceUpdate()` instead.
   *
   * This method doesn't expect any request body.
   */
  postFirmwareForceUpdate$Response(params: PostFirmwareForceUpdate$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return postFirmwareForceUpdate(this.http, this.rootUrl, params, context);
  }

  /**
   * Forces the router to update to a defined firmware.
   *
   * The router will start with the firmware update process after the next status communication. The user does not need to download or activate the firmware afterwards.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `postFirmwareForceUpdate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postFirmwareForceUpdate(params: PostFirmwareForceUpdate$Params, context?: HttpContext): Observable<void> {
    return this.postFirmwareForceUpdate$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `getAllRouters()` */
  static readonly GetAllRoutersPath = '/routers';

  /**
   * List all devices.
   *
   * Returns a list of devices, assigned to the tenant of the requesting user. Or all for administrators.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllRouters()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllRouters$Response(params?: GetAllRouters$Params, context?: HttpContext): Observable<StrictHttpResponse<PageDto>> {
    return getAllRouters(this.http, this.rootUrl, params, context);
  }

  /**
   * List all devices.
   *
   * Returns a list of devices, assigned to the tenant of the requesting user. Or all for administrators.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllRouters$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllRouters(params?: GetAllRouters$Params, context?: HttpContext): Observable<PageDto> {
    return this.getAllRouters$Response(params, context).pipe(
      map((r: StrictHttpResponse<PageDto>): PageDto => r.body)
    );
  }

  /** Path part for operation `postNewRouter()` */
  static readonly PostNewRouterPath = '/routers';

  /**
   * Register a new device.
   *
   * Create a new device
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postNewRouter()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postNewRouter$Response(params?: PostNewRouter$Params, context?: HttpContext): Observable<StrictHttpResponse<RouterCreationResponse>> {
    return postNewRouter(this.http, this.rootUrl, params, context);
  }

  /**
   * Register a new device.
   *
   * Create a new device
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `postNewRouter$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postNewRouter(params?: PostNewRouter$Params, context?: HttpContext): Observable<RouterCreationResponse> {
    return this.postNewRouter$Response(params, context).pipe(
      map((r: StrictHttpResponse<RouterCreationResponse>): RouterCreationResponse => r.body)
    );
  }

  /** Path part for operation `postNewList()` */
  static readonly PostNewListPath = '/routers/list';

  /**
   * Register a new list of devices.
   *
   * Create all devices mentioned in the list. Returns a List of responses containing if the specific router (identified by its serialnumber) was created
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postNewList()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postNewList$Response(params?: PostNewList$Params, context?: HttpContext): Observable<StrictHttpResponse<Array<RouterCreationResponse>>> {
    return postNewList(this.http, this.rootUrl, params, context);
  }

  /**
   * Register a new list of devices.
   *
   * Create all devices mentioned in the list. Returns a List of responses containing if the specific router (identified by its serialnumber) was created
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `postNewList$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  postNewList(params?: PostNewList$Params, context?: HttpContext): Observable<Array<RouterCreationResponse>> {
    return this.postNewList$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<RouterCreationResponse>>): Array<RouterCreationResponse> => r.body)
    );
  }

  /** Path part for operation `getRouterById()` */
  static readonly GetRouterByIdPath = '/routers/{id}';

  /**
   * Returns a specific device.
   *
   * Returns the device belonging on the given ID
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRouterById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRouterById$Response(params: GetRouterById$Params, context?: HttpContext): Observable<StrictHttpResponse<RouterDto>> {
    return getRouterById(this.http, this.rootUrl, params, context);
  }

  /**
   * Returns a specific device.
   *
   * Returns the device belonging on the given ID
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRouterById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRouterById(params: GetRouterById$Params, context?: HttpContext): Observable<RouterDto> {
    return this.getRouterById$Response(params, context).pipe(
      map((r: StrictHttpResponse<RouterDto>): RouterDto => r.body)
    );
  }

  /** Path part for operation `updateRouter()` */
  static readonly UpdateRouterPath = '/routers/{id}';

  /**
   * Update a device.
   *
   * Update a device
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRouter()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateRouter$Response(params: UpdateRouter$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return updateRouter(this.http, this.rootUrl, params, context);
  }

  /**
   * Update a device.
   *
   * Update a device
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRouter$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateRouter(params: UpdateRouter$Params, context?: HttpContext): Observable<void> {
    return this.updateRouter$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `deleteRouterById()` */
  static readonly DeleteRouterByIdPath = '/routers/{id}';

  /**
   * Delete a specific device.
   *
   * Delete a device given that the user has the necessary permissions and the device exists
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteRouterById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteRouterById$Response(params: DeleteRouterById$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return deleteRouterById(this.http, this.rootUrl, params, context);
  }

  /**
   * Delete a specific device.
   *
   * Delete a device given that the user has the necessary permissions and the device exists
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteRouterById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteRouterById(params: DeleteRouterById$Params, context?: HttpContext): Observable<void> {
    return this.deleteRouterById$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `postActivation()` */
  static readonly PostActivationPath = '/routers/{id}/activation';

  /**
   * Starts the activation process of a router.
   *
   * If the router is in an registered state, it needs to do a CSR to be able to connect via VPN. This activation call permits the router to do a CSR.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postActivation()` instead.
   *
   * This method doesn't expect any request body.
   */
  postActivation$Response(params: PostActivation$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return postActivation(this.http, this.rootUrl, params, context);
  }

  /**
   * Starts the activation process of a router.
   *
   * If the router is in an registered state, it needs to do a CSR to be able to connect via VPN. This activation call permits the router to do a CSR.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `postActivation$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postActivation(params: PostActivation$Params, context?: HttpContext): Observable<void> {
    return this.postActivation$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `postCertificateRenewal()` */
  static readonly PostCertificateRenewalPath = '/routers/{id}/certificate/allow-renewal';

  /**
   * Allow the router to renew the VPN certificate.
   *
   * The router will start with the certificate renewal after the next status communication. The renewal is only possible in renewal or grace period.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postCertificateRenewal()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCertificateRenewal$Response(params: PostCertificateRenewal$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return postCertificateRenewal(this.http, this.rootUrl, params, context);
  }

  /**
   * Allow the router to renew the VPN certificate.
   *
   * The router will start with the certificate renewal after the next status communication. The renewal is only possible in renewal or grace period.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `postCertificateRenewal$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postCertificateRenewal(params: PostCertificateRenewal$Params, context?: HttpContext): Observable<void> {
    return this.postCertificateRenewal$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `getMasterPasswordById()` */
  static readonly GetMasterPasswordByIdPath = '/routers/{id}/master-password';

  /**
   * Returns the master password for a specific device.
   *
   * The master password for a specific device will be return. It is not part of the normal GET call of a device.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMasterPasswordById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMasterPasswordById$Response(params: GetMasterPasswordById$Params, context?: HttpContext): Observable<StrictHttpResponse<string>> {
    return getMasterPasswordById(this.http, this.rootUrl, params, context);
  }

  /**
   * Returns the master password for a specific device.
   *
   * The master password for a specific device will be return. It is not part of the normal GET call of a device.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMasterPasswordById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMasterPasswordById(params: GetMasterPasswordById$Params, context?: HttpContext): Observable<string> {
    return this.getMasterPasswordById$Response(params, context).pipe(
      map((r: StrictHttpResponse<string>): string => r.body)
    );
  }

  /** Path part for operation `postVpnConnection()` */
  static readonly PostVpnConnectionPath = '/routers/{id}/vpn-connection';

  /**
   * Connect a device via VPN.
   *
   * Connects a specific device via VPN
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `postVpnConnection()` instead.
   *
   * This method doesn't expect any request body.
   */
  postVpnConnection$Response(params: PostVpnConnection$Params, context?: HttpContext): Observable<StrictHttpResponse<boolean>> {
    return postVpnConnection(this.http, this.rootUrl, params, context);
  }

  /**
   * Connect a device via VPN.
   *
   * Connects a specific device via VPN
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `postVpnConnection$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  postVpnConnection(params: PostVpnConnection$Params, context?: HttpContext): Observable<boolean> {
    return this.postVpnConnection$Response(params, context).pipe(
      map((r: StrictHttpResponse<boolean>): boolean => r.body)
    );
  }

  /** Path part for operation `deleteVpnConnection()` */
  static readonly DeleteVpnConnectionPath = '/routers/{id}/vpn-connection';

  /**
   * Disconnect from a device VPN.
   *
   * Disconnects from a specific device VPN. Can be called by the user, who connected the VPN.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteVpnConnection()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteVpnConnection$Response(params: DeleteVpnConnection$Params, context?: HttpContext): Observable<StrictHttpResponse<boolean>> {
    return deleteVpnConnection(this.http, this.rootUrl, params, context);
  }

  /**
   * Disconnect from a device VPN.
   *
   * Disconnects from a specific device VPN. Can be called by the user, who connected the VPN.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteVpnConnection$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteVpnConnection(params: DeleteVpnConnection$Params, context?: HttpContext): Observable<boolean> {
    return this.deleteVpnConnection$Response(params, context).pipe(
      map((r: StrictHttpResponse<boolean>): boolean => r.body)
    );
  }

  /** Path part for operation `unlinkRouter()` */
  static readonly UnlinkRouterPath = '/routers/{id}/vpn-connection';

  /**
   * Disconnect a device when there is no user.
   *
   * Changes device status to unlinked. Can only be used when the device does not have a router link.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `unlinkRouter()` instead.
   *
   * This method doesn't expect any request body.
   */
  unlinkRouter$Response(params: UnlinkRouter$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return unlinkRouter(this.http, this.rootUrl, params, context);
  }

  /**
   * Disconnect a device when there is no user.
   *
   * Changes device status to unlinked. Can only be used when the device does not have a router link.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `unlinkRouter$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  unlinkRouter(params: UnlinkRouter$Params, context?: HttpContext): Observable<void> {
    return this.unlinkRouter$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `routersIdVpnConnectionUsernameDelete()` */
  static readonly RoutersIdVpnConnectionUsernameDeletePath = '/routers/{id}/vpn-connection/{username}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `routersIdVpnConnectionUsernameDelete()` instead.
   *
   * This method doesn't expect any request body.
   */
  routersIdVpnConnectionUsernameDelete$Response(params: RoutersIdVpnConnectionUsernameDelete$Params, context?: HttpContext): Observable<StrictHttpResponse<boolean>> {
    return routersIdVpnConnectionUsernameDelete(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `routersIdVpnConnectionUsernameDelete$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  routersIdVpnConnectionUsernameDelete(params: RoutersIdVpnConnectionUsernameDelete$Params, context?: HttpContext): Observable<boolean> {
    return this.routersIdVpnConnectionUsernameDelete$Response(params, context).pipe(
      map((r: StrictHttpResponse<boolean>): boolean => r.body)
    );
  }

}
